"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "../Button";
import { CloseIcon } from "../icons/CloseIcon";

type DrawerSize = "sm" | "md" | "lg";
type DrawerSide = "start" | "end";
type IconTone = "accent" | "muted";
type BodyTone = "surface" | "surface-alt";

const widthClass: Record<DrawerSize, string> = {
  sm: "w-full max-w-[420px]",
  md: "w-full max-w-[540px]",
  lg: "w-full max-w-[680px]",
};

export type DrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Drawer width tier — sm/md/lg. Default md (540px). */
  size?: DrawerSize;
  /** Slide-in side. Default "end" (right in LTR, left in RTL). */
  side?: DrawerSide;
  /**
   * Body background. Default "surface-alt" (sand) — drawer bodies render
   * white SectionCards on a sand backdrop so every drawer reads as the same
   * design. Pass "surface" if a specific drawer needs a flat white body.
   */
  bodyTone?: BodyTone;
  /** Small icon tile in the header (32×32). */
  icon?: ReactNode;
  /** "accent" (soft amber, default) or "muted" (sand). */
  iconTone?: IconTone;
  title?: ReactNode;
  subtitle?: ReactNode;
  closeLabel?: string;
  /** Slot before the icon (e.g. back button on multi-view drawers). */
  headerLeading?: ReactNode;
  /** Slot between title block and close X (e.g. status badge). */
  headerTrailing?: ReactNode;
  /** When provided, replaces the icon + title + subtitle stack entirely. */
  headerOverride?: ReactNode;
  /** Hint text on the left of the footer (12px muted). */
  footerLeft?: ReactNode;
  /** Action buttons on the right of the footer. */
  footerActions?: ReactNode;
  hideFooter?: boolean;
  /** Conditionally render the footer (e.g. sticky-when-dirty). */
  showFooterWhen?: boolean;
  children: ReactNode;
};

const CLOSE_DURATION_MS = 380;

/**
 * Side-sliding drawer with the same chrome language as `<Modal>` — icon tile
 * + title + subtitle in the header, scrollable body, optional footer with
 * `footerLeft` + `footerActions`. Handles backdrop click, Escape, focus trap,
 * and body scroll-lock just like `ModalSheet`. Use this instead of rolling
 * your own slide-in panel so every drawer in the app shares the same look.
 */
export function Drawer({
  isOpen,
  onClose,
  size = "md",
  side = "end",
  bodyTone = "surface-alt",
  icon,
  iconTone = "accent",
  title,
  subtitle,
  closeLabel = "Close",
  headerLeading,
  headerTrailing,
  headerOverride,
  footerLeft,
  footerActions,
  hideFooter = false,
  showFooterWhen,
  children,
}: DrawerProps) {
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Two-effect mount/visibility sequence so the open animation always plays:
  //
  // Effect 1 — mount the panel as soon as isOpen flips true.
  // Effect 2 — once the panel is actually in the DOM (`isMounted` committed
  //            and the closed-state class painted), schedule the visibility
  //            transition on the next animation frame.
  //
  // Splitting the steps guarantees the browser paints the `translate-x-full`
  // (off-screen) state on one frame and `translate-x-0` (on-screen) on the
  // next, with the CSS transition interpolating between them. Doing both in
  // the same effect can collapse into a single paint frame under React 18's
  // automatic batching, which makes the slide-in animation get skipped.
  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isMounted) return;
    if (isOpen) {
      const id1 = requestAnimationFrame(() => {
        const id2 = requestAnimationFrame(() => setIsVisible(true));
        (closeTimerRef as unknown as { current: number }).current =
          id2 as unknown as number;
      });
      return () => cancelAnimationFrame(id1);
    }
    setIsVisible(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(
      () => setIsMounted(false),
      CLOSE_DURATION_MS,
    );
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [isOpen, isMounted]);

  // Escape to close.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current?.();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen]);

  // Body scroll lock.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Focus trap — focus first interactive element on open, restore on close.
  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement;
    const id = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length > 0) focusable[0].focus();
    });
    return () => {
      cancelAnimationFrame(id);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [isOpen]);

  if (!isMounted) return null;

  const footerVisible =
    !hideFooter && (showFooterWhen === undefined || showFooterWhen);

  const iconTileClass =
    iconTone === "accent"
      ? "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-[color-mix(in_oklab,var(--accent-500)_14%,white)] text-[color:var(--accent-600)]"
      : "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-[color:var(--surface-alt)] text-[color:var(--text-muted)]";

  const sidePosition =
    side === "end"
      ? "end-0 border-s border-[color:var(--border-light)]"
      : "start-0 border-e border-[color:var(--border-light)]";

  const transformClass = isVisible
    ? "translate-x-0"
    : side === "end"
      ? "rtl:-translate-x-full translate-x-full"
      : "rtl:translate-x-full -translate-x-full";

  return (
    <>
      <div
        onClick={() => onCloseRef.current?.()}
        aria-hidden="true"
        className={[
          "fixed inset-0 z-40 bg-black/40 transition-opacity",
          isVisible
            ? "opacity-100 duration-[380ms]"
            : "opacity-0 duration-[280ms]",
        ].join(" ")}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        className={[
          "fixed top-0 bottom-0 z-50 flex flex-col bg-[color:var(--surface)] shadow-[var(--shadow-lg,0_24px_64px_rgba(11,18,32,0.4))]",
          // Transition both `transform` and `translate` properties — Tailwind
          // v4's `translate-x-*` utilities use CSS `translate:` (not `transform:
          // translateX()`), so `transition-transform` alone wouldn't animate
          // the slide.
          "[transition-property:transform,translate] duration-[380ms] ease-[cubic-bezier(0.2,0.9,0.2,1)]",
          sidePosition,
          widthClass[size],
          transformClass,
        ].join(" ")}
      >
        <header className="flex shrink-0 items-center gap-3 border-b border-[color:var(--border-light)] bg-[color:var(--surface)] px-5 py-4">
          {headerLeading ? (
            <span className="shrink-0">{headerLeading}</span>
          ) : null}
          {headerOverride ? (
            <div className="flex min-w-0 flex-1 items-center">
              {headerOverride}
            </div>
          ) : (
            <>
              {icon ? <span className={iconTileClass}>{icon}</span> : null}
              <div className="flex min-w-0 flex-1 flex-col gap-0.5 leading-tight">
                {title ? (
                  <span className="truncate text-[15px] font-semibold text-[color:var(--text-primary)]">
                    {title}
                  </span>
                ) : null}
                {subtitle ? (
                  <span className="truncate text-[12px] text-[color:var(--text-muted)]">
                    {subtitle}
                  </span>
                ) : null}
              </div>
            </>
          )}
          {headerTrailing ? (
            <span className="shrink-0">{headerTrailing}</span>
          ) : null}
          <Button
            variant="link"
            size="sm"
            onClick={onClose}
            aria-label={closeLabel}
            leadingIcon={<CloseIcon size={18} />}
          />
        </header>
        <div
          className={[
            "flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-5 py-5",
            bodyTone === "surface-alt"
              ? "bg-[color:var(--surface-alt)]"
              : "bg-[color:var(--surface)]",
          ].join(" ")}
        >
          {children}
        </div>
        {footerVisible ? (
          <footer className="flex shrink-0 items-center gap-3 border-t border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-5 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
            <span className="text-[12px] text-[color:var(--text-muted)]">
              {footerLeft}
            </span>
            <div className="ms-auto flex items-center gap-2">
              {footerActions}
            </div>
          </footer>
        ) : null}
      </aside>
    </>
  );
}
